/**
 * Сессия пользователя в localStorage: токен и публичные данные для мастхеда.
 *
 * Токен выдаёт `POST /api/auth/login` (`src/frontend/api.ts`). Профиль сохраняется
 * рядом с токеном, чтобы после перезагрузки страницы восстановить вход (мастхед с
 * именем и ролями) без запроса к API — тех же данных, что вернул вход. Профиль
 * прежнего формата (одна роль вместо набора) считается битым и удаляется: роли
 * приходят с сервера только при входе.
 *
 * Ограничение: серверной проверки токена на старте нет. Устаревший или отозванный
 * токен отсечёт первый защищённый запрос API (задача 3) — тогда сессию нужно
 * очистить вызовом `clearSession()`. Когда появится эндпоинт проверки сессии,
 * наполнение мастхеда переедет на него, а этот модуль останется местом хранения.
 */
import { isUserRole } from '../shared/roles';
import type { PublicUser } from '../shared/types';

/** Ключ localStorage с токеном сессии: его подставляет каждый защищённый запрос. */
export const SESSION_TOKEN_KEY = 'eleonora.sessionToken';

/** Ключ localStorage с публичными данными пользователя (для мастхеда после перезагрузки). */
export const SESSION_USER_KEY = 'eleonora.sessionUser';

/** Восстановленная сессия: токен для запросов и данные пользователя для мастхеда. */
export interface Session {
  /** Токен сессии из ответа входа. */
  token: string;
  /** Публичные данные пользователя (без хеша пароля). */
  user: PublicUser;
}

/**
 * Проверяет, что разобранное из localStorage значение — полноценный PublicUser.
 * Набор ролей обязан состоять из известных ролей: иначе профиль считается битым.
 */
function isPublicUser(value: unknown): value is PublicUser {
  if (typeof value !== 'object' || value === null) return false;
  const user = value as Record<string, unknown>;
  return (
    typeof user.id === 'string' &&
    typeof user.firstName === 'string' &&
    typeof user.lastName === 'string' &&
    typeof user.organizationName === 'string' &&
    typeof user.email === 'string' &&
    typeof user.emailConfirmed === 'boolean' &&
    Array.isArray(user.roles) &&
    user.roles.every((role) => isUserRole(role))
  );
}

/** Сохраняет сессию после успешного входа: токен и профиль для мастхеда. */
export function saveSession(token: string, user: PublicUser): void {
  localStorage.setItem(SESSION_TOKEN_KEY, token);
  localStorage.setItem(SESSION_USER_KEY, JSON.stringify(user));
}

/**
 * Токен сессии для заголовка `X-Eleonora-Session: Bearer …` (src/shared/sessionHeader.ts)
 * или null, если пользователь не вошёл. Клиент API (`src/frontend/api.ts`) подставляет
 * его в каждый защищённый запрос заявки.
 */
export function getSessionToken(): string | null {
  return localStorage.getItem(SESSION_TOKEN_KEY);
}

/**
 * Читает сохранённую сессию для восстановления входа. Битые или частичные данные
 * (правка localStorage, смена формата профиля) сессией не считаются и удаляются —
 * «самолечение»: пользователь просто увидит экран входа, а не сломанный мастхед.
 */
export function loadSession(): Session | null {
  const token = localStorage.getItem(SESSION_TOKEN_KEY);
  const rawUser = localStorage.getItem(SESSION_USER_KEY);
  if (!token || !rawUser) {
    // Один ключ без другого бесполезен — убираем остаток, чтобы состояние не «залипало».
    if (token || rawUser) clearSession();
    return null;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(rawUser);
  } catch {
    clearSession();
    return null;
  }
  if (!isPublicUser(parsed)) {
    clearSession();
    return null;
  }
  return { token, user: parsed };
}

/** Удаляет сессию: выход из аккаунта и отказ сервера авторизовать запрос (401). */
export function clearSession(): void {
  localStorage.removeItem(SESSION_TOKEN_KEY);
  localStorage.removeItem(SESSION_USER_KEY);
}
